import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject, combineLatest, merge } from 'rxjs';
import { filter, map, withLatestFrom, distinctUntilChanged, switchMap, startWith } from 'rxjs/operators';
import { unbox } from 'ngrx-forms';
import isEqual from 'lodash-es/isEqual';
import { Actions } from '@ngrx/effects';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { onZefDialogClose, selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { MergeStrategy, successOf } from '@zerops/zef/entities';
import { selectZefProgressByType, selectZefProgressMapByType } from '@zerops/zef/progress';
import { UserTokenEntity, UserTokenRequest } from '@zerops/zerops/core/user-token-base';
import { IntegrationTokenEntity } from '@zerops/zerops/core/integration-token-base';
import {
  IntegrationTokenDelegationEntity,
  integrationTokenDelegationActions,
  delegationTokenTag
} from '@zerops/zerops/core/integration-token-delegation-base';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import { formValueOnValid } from '@zerops/zef/forms';
import {
  IntegrationTokenRoleCode,
  TOKEN_ROLE_OPTIONS,
  TokenRoleOption,
  IntegrationTokenRequest,
  IntegrationTokenProjectAccess,
  IntegrationTokenDelegationRequest,
  IntegrationTokenDelegationRoleCode,
  DELEGATION_ROLE_LABELS
} from '@zerops/models/integration-token';
import { ClientUser, ClientUserRole, hasBasicUserRoleOrHigher } from '@zerops/models/client-user';
import { Project, ProjectStatuses } from '@zerops/models/project';
import { FEATURE_NAME, SUDO_TOKEN_TOOLTIP } from './token-add-edit-dialog.constant';
import {
  TokenAddEditDialogMeta,
  TokenAddEditMode
} from './token-add-edit.model';
import { TokenAddEditForm, TokenAddEditFormValue } from './token-add-edit.form';
import { DelegationCreateForm, DelegationCreateFormValue } from './token-delegation.form';


@Component({
  selector: 'z-token-add-edit-dialog',
  templateUrl: './token-add-edit-dialog.feature.html',
  styleUrl: './token-add-edit-dialog.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TokenAddEditDialogFeature extends ZefReactiveComponent {
  // # Deps
  #store = inject(Store);
  #userTokenEntity = inject(UserTokenEntity);
  #integrationTokenEntity = inject(IntegrationTokenEntity);
  #delegationEntity = inject(IntegrationTokenDelegationEntity);
  #userEntity = inject(UserEntity);
  #projectEntity = inject(ProjectEntity);
  #form = inject(TokenAddEditForm);
  #delegationForm = inject(DelegationCreateForm);
  #actions$ = inject(Actions);

  // # Event Streams
  onSubmit$ = new Subject<void>();
  onClose$ = new Subject<void>();
  onDeleteDelegation$ = new Subject<string>();
  onOpenCreateDelegation$ = new Subject<void>();
  onCancelCreateDelegation$ = new Subject<void>();
  onSubmitDelegation$ = new Subject<void>();

  // View-state for the inline delegations section (pure UI, no store needed)
  expandedDelegationId = signal<string | undefined>(undefined);

  // Host element of the delegation create form, used to scroll it into view on open
  delegationCreateAnchor = viewChild('delegationCreateAnchor', { read: ElementRef });

  // # Data
  // -- sync
  key = FEATURE_NAME;
  tokenAddEditModes = TokenAddEditMode;
  addUserTokenKey = this.#userTokenEntity.addOne.type;
  updateUserTokenKey = this.#userTokenEntity.updateOne.type;
  addIntegrationTokenKey = this.#integrationTokenEntity.addOne.type;
  updateIntegrationTokenKey = this.#integrationTokenEntity.updateOne.type;
  addDelegationKey = this.#delegationEntity.addOne.type;
  deleteDelegationKey = this.#delegationEntity.deleteOne.type;
  delegationRoleLabels = DELEGATION_ROLE_LABELS;
  emptyMap = new Map<string, boolean>();
  sudoTooltip = SUDO_TOKEN_TOOLTIP;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data.state));
  meta$ = this.dialogState$.pipe(
    filter((d) => !!d.meta),
    map((data) => data.meta as TokenAddEditDialogMeta)
  );
  mode$ = this.meta$.pipe(
    filter((d) => !!d.mode),
    map(({ mode }) => mode),
    distinctUntilChanged()
  );
  tokenId$ = this.dialogState$.pipe(
    map((d) => d?.meta?.tokenId),
    distinctUntilChanged()
  );
  isEditMode$ = this.mode$.pipe(
    map((mode) => mode === TokenAddEditMode.EDIT_INTEGRATION || mode === TokenAddEditMode.EDIT_USER)
  );
  isEditIntegration$ = this.mode$.pipe(
    map((mode) => mode === TokenAddEditMode.EDIT_INTEGRATION)
  );
  isUserToken$ = this.mode$.pipe(
    map((mode) => mode === TokenAddEditMode.ADD_USER || mode === TokenAddEditMode.EDIT_USER)
  );
  activeClientId$ = this.#userEntity.activeClientId$;
  activeClientUser$ = this.#userEntity.activeClientUser$;
  allProjects$ = this.#projectEntity.list$();
  formState$ = this.#form.state$;

  // Available token roles based on current user
  availableTokenRoles$ = this.activeClientUser$.pipe(
    map(user => this.#getAvailableTokenRoles(user))
  );

  // Available projects based on current user
  availableProjects$ = combineLatest([
    this.activeClientUser$,
    this.allProjects$
  ]).pipe(
    map(([ user, projects ]) => this.#getAvailableProjects(user, projects))
  );

  // Show integration-specific fields for ADD_INTEGRATION and EDIT_INTEGRATION modes
  showIntegrationFields$ = this.isUserToken$.pipe(
    map((isUserToken) => !isUserToken)
  );

  // Hide create projects toggle for GUEST users (NO_ACCESS + canCreateProjects=false)
  hideCreateProjectsToggle$ = this.activeClientUser$.pipe(
    map((user) =>
      user?.roleCode === ClientUserRole.NoAccess && !user?.canCreateProjects
    )
  );

  // Original projects from entity store (for change tracking in edit mode)
  originalProjects$ = this.tokenId$.pipe(
    filter((id) => !!id),
    switchMap((id) => this.#integrationTokenEntity.entityById$(id)),
    map((token) => token?.projects || []),
    distinctUntilChanged((a, b) => isEqual(a, b))
  );

  // Current projects from form
  currentProjects$ = this.#form.value$.pipe(
    map((value) => unbox(value.projects)),
    distinctUntilChanged((a, b) => isEqual(a, b))
  );

  // Changes map: Map<projectId, boolean> (only in edit mode)
  projectChangesMap$ = combineLatest([
    this.isEditIntegration$,
    this.originalProjects$.pipe(startWith([])),
    this.currentProjects$
  ]).pipe(
    map(([ isEditMode, original, current ]) =>
      isEditMode ? this.#computeProjectChanges(original, current) : new Map()
    ),
    distinctUntilChanged((a, b) => isEqual(Array.from(a.entries()), Array.from(b.entries())))
  );

  // -- delegations (edit mode only), scoped to this token via a per-token cache tag
  tokenDelegations$ = this.tokenId$.pipe(
    filter((id) => !!id),
    distinctUntilChanged(),
    switchMap((tokenId) => this.#delegationEntity.list$(delegationTokenTag(tokenId))),
    distinctUntilChanged((a, b) => isEqual(a, b))
  );
  delegationsLoading$ = this.#store.pipe(
    select(selectZefProgressByType(integrationTokenDelegationActions.loadTokenDelegations.type)),
    map((progress) => !!progress)
  );
  deleteDelegationProgressesMap$ = this.#store.pipe(
    select(selectZefProgressMapByType(this.#delegationEntity.deleteOne.type))
  );
  delegationFormState$ = this.#delegationForm.state$;
  projectsNameMap$ = this.allProjects$.pipe(
    map((projects) => (projects || []).reduce((acc, project) => {
      acc.set(project.id, project.name);
      return acc;
    }, new Map<string, string>()))
  );

  // Create-form visibility: opened by the button, closed on cancel / success /
  // when the dialog itself is closed (so reopening lands back on the list view)
  #onDelegationAddSuccess$ = this.#actions$.pipe(successOf(this.#delegationEntity.addOne));
  delegationCreateOpen$ = merge(
    this.onOpenCreateDelegation$.pipe(map(() => true)),
    this.onCancelCreateDelegation$.pipe(map(() => false)),
    this.#onDelegationAddSuccess$.pipe(map(() => false)),
    this.#actions$.pipe(onZefDialogClose(FEATURE_NAME), map(() => false))
  ).pipe(
    startWith(false),
    distinctUntilChanged()
  );

  // # State resolver
  state = this.$connect({
    open: this.open$,
    mode: this.mode$,
    isEditMode: this.isEditMode$,
    isEditIntegration: this.isEditIntegration$,
    isUserToken: this.isUserToken$,
    showIntegrationFields: this.showIntegrationFields$,
    hideCreateProjectsToggle: this.hideCreateProjectsToggle$,
    activeClientId: this.activeClientId$,
    currentUser: this.activeClientUser$,
    availableTokenRoles: this.availableTokenRoles$,
    availableProjects: this.availableProjects$,
    formState: this.formState$,
    projectChangesMap: this.projectChangesMap$,
    tokenDelegations: this.tokenDelegations$,
    delegationsLoading: this.delegationsLoading$,
    deleteDelegationProgressesMap: this.deleteDelegationProgressesMap$,
    delegationFormState: this.delegationFormState$,
    projectsNameMap: this.projectsNameMap$,
    delegationCreateOpen: this.delegationCreateOpen$
  });

  constructor() {
    super();

    // # Dispatcher
    this.$dispatchActions([
      this.onClose$.pipe(map(() => zefDialogClose({ key: FEATURE_NAME }))),

      // NOTE: Mode setting moved to effects (onDialogOpenSetMode$)

      this.onSubmit$.pipe(
        formValueOnValid(this.#form),
        withLatestFrom(this.mode$, this.tokenId$, this.activeClientId$),
        map(([ formValue, mode, tokenId, clientId ]) => {
          // EDIT_INTEGRATION: use tokenId from metadata
          if (mode === TokenAddEditMode.EDIT_INTEGRATION) {
            return this.#integrationTokenEntity.updateOne(
              tokenId,
              this.#transformFormData(formValue),
              {
                clientId,
                zefEntityMergeStrategy: {
                  projects: MergeStrategy.KeepNew
                }
              }
            );
          }

          if (mode === TokenAddEditMode.EDIT_USER) {
            return this.#userTokenEntity.updateOne(tokenId, this.#transformUserFormData(formValue));
          }

          if (mode === TokenAddEditMode.ADD_USER) {
            return this.#userTokenEntity.addOne(this.#transformUserFormData(formValue));
          }

          // ADD_INTEGRATION: full payload
          return this.#integrationTokenEntity.addOne(
            this.#transformFormData(formValue),
            { clientId }
          );
        })
      ),

      // Reset the create form each time it is opened
      this.onOpenCreateDelegation$.pipe(map(() => this.#delegationForm.reset())),
      this.onOpenCreateDelegation$.pipe(map(() => this.#delegationForm.setDefaultValues())),

      // Restricted users (single available role, e.g. NO_ACCESS) can only issue a
      // delegation with that role — pre-select it so the form matches the selector.
      this.onOpenCreateDelegation$.pipe(
        withLatestFrom(this.availableTokenRoles$),
        filter(([ , roles ]) => roles?.length === 1),
        map(([ , roles ]) => this.#delegationForm.setValue('roleCode', roles[0].value))
      ),

      this.onSubmitDelegation$.pipe(
        formValueOnValid(this.#delegationForm),
        withLatestFrom(this.tokenId$, this.activeClientId$),
        map(([ formValue, tokenId, clientId ]) =>
          this.#delegationEntity.addOne(
            this.#transformDelegationFormData(formValue),
            { clientId, tokenId }
          )
        )
      ),

      this.onDeleteDelegation$.pipe(
        withLatestFrom(this.tokenId$, this.activeClientId$),
        map(([ delegationId, tokenId, clientId ]) =>
          this.#delegationEntity.deleteOne(delegationId, { clientId, tokenId })
        )
      ),

      // Refresh this token's scoped list after a delegation is created or deleted
      merge(
        this.#actions$.pipe(successOf(this.#delegationEntity.addOne)),
        this.#actions$.pipe(successOf(this.#delegationEntity.deleteOne))
      ).pipe(
        withLatestFrom(this.tokenId$),
        filter(([ , tokenId ]) => !!tokenId),
        map(([ , tokenId ]) => integrationTokenDelegationActions.loadTokenDelegations({ tokenId }))
      )
    ]);

    // The create form can open below the fold (no delegations yet / short viewport);
    // scroll it into view as soon as it renders.
    effect(() => {
      const anchor = this.delegationCreateAnchor();
      if (anchor) {
        (anchor.nativeElement as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  #getAvailableTokenRoles(user: ClientUser | null): TokenRoleOption[] {
    if (!user) return [];

    // OWNER/ADMIN can create tokens with any role
    if (user.roleCode === ClientUserRole.Owner || user.roleCode === ClientUserRole.Admin) {
      return TOKEN_ROLE_OPTIONS;
    }

    // NO_ACCESS (Developer or Guest) can only create per-project tokens
    // Both Developer (canCreateProjects=true) and Guest (canCreateProjects=false)
    // get the NoAccess token role option
    if (user.roleCode === ClientUserRole.NoAccess) {
      return TOKEN_ROLE_OPTIONS.filter((opt) =>
        opt.value === IntegrationTokenRoleCode.NoAccess
      );
    }

    // READ_ONLY cannot create tokens with meaningful permissions
    return TOKEN_ROLE_OPTIONS.filter((opt) => opt.value === IntegrationTokenRoleCode.ReadOnly);
  }

  #getAvailableProjects(user: ClientUser | null, projects: Project[]): Project[] {
    if (!user || !projects) return [];

    const activeProjects = projects.filter((p) => p.status === ProjectStatuses.Active);

    // OWNER/ADMIN/BASIC USER see all projects
    if (hasBasicUserRoleOrHigher(user.roleCode)) {
      return activeProjects;
    }

    // NO_ACCESS (Developer or Guest) sees only assigned projects
    // Both get filtered to projects where they have a role assignment
    if (user.roleCode === ClientUserRole.NoAccess) {
      return activeProjects.filter((p) =>
        p.userRoles?.some((ur) => ur.clientUserId === user.id)
      );
    }

    return [];
  }

  #transformFormData(formValue: TokenAddEditFormValue): IntegrationTokenRequest {
    return {
      name: formValue.name,
      roleCode: formValue.roleCode ?? IntegrationTokenRoleCode.NoAccess,
      canCreateProjects: formValue.canCreateProjects,
      projects: unbox(formValue.projects),
      sudo: formValue.sudo
    };
  }

  #transformUserFormData(formValue: TokenAddEditFormValue): UserTokenRequest {
    return {
      name: formValue.name,
      sudo: formValue.sudo
    };
  }

  // The create UI uses the three-role token model; map it onto the delegation
  // request (whose role type spans the full five-role range).
  #transformDelegationFormData(formValue: DelegationCreateFormValue): IntegrationTokenDelegationRequest {
    return {
      roleCode: formValue.roleCode as unknown as IntegrationTokenDelegationRoleCode,
      canCreateProjects: formValue.canCreateProjects,
      sudo: formValue.sudo,
      projectPermissions: unbox(formValue.projects).map((project) => ({
        projectId: project.projectId,
        roleCode: project.roleCode as unknown as IntegrationTokenDelegationRoleCode
      }))
    };
  }

  /**
   * Compute which projects have changed roles or been added/removed.
   *
   * Change Detection Rules:
   * 1. Project in current but not in original → NEW (highlight)
   * 2. Project in both, different roleCode → MODIFIED (highlight)
   * 3. Project in original but not in current → REMOVED (no highlight needed, it's gone from list)
   *
   * Uses O(n) lookup tables for optimal performance.
   *
   * @returns Map where key=projectId, value=true if changed
   */
  #computeProjectChanges(
    original: IntegrationTokenProjectAccess[],
    current: IntegrationTokenProjectAccess[]
  ): Map<string, boolean> {
    const changes = new Map<string, boolean>();

    // O(n) lookup tables
    const originalMap = new Map(original.map((p) => [ p.projectId, p.roleCode ]));
    const currentMap = new Map(current.map((p) => [ p.projectId, p.roleCode ]));

    // Check current vs original (new or modified)
    currentMap.forEach((roleCode, projectId) => {
      const origRole = originalMap.get(projectId);
      if (!origRole || origRole !== roleCode) {
        changes.set(projectId, true);
      }
    });

    // Check removed (optional: highlight removed state if needed in future)
    // Currently not needed as removed projects don't appear in the list
    originalMap.forEach((_, projectId) => {
      if (!currentMap.has(projectId)) {
        // Project was removed - could set flag here if needed
        // changes.set(projectId, true);
      }
    });

    return changes;
  }
}
